import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';

/**
 * Settings hub at /settings. Single page that links every connection,
 * integration, and admin flow with live status.
 */
export function SettingsHub() {
  const { user, role, mfaEnrolled, signOut } = useAuth();
  const isAdmin = role === 'admin';
  const [dropboxConnected, setDropboxConnected] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, 'integrations', user.uid), (snap) => {
      setDropboxConnected(!!snap.data()?.dropbox?.refreshToken);
    });
  }, [user]);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-6">
      <header>
        <div className="text-[10px] uppercase tracking-wider text-stone-400">Account</div>
        <h1 className="text-2xl font-semibold text-stone-800">Settings</h1>
        <p className="mt-1 text-sm text-stone-500">
          Connect data sources, manage who has access, and tune your security posture.
        </p>
      </header>

      <Card title="Signed in as">
        <Row
          label={user?.email ?? '—'}
          value={role ?? 'no role'}
          actionLabel="Sign out"
          onClick={() => void signOut()}
        />
      </Card>

      <Card title="Two-factor authentication">
        <Row
          label="SMS code on every sign-in"
          value={mfaEnrolled ? 'Enrolled' : 'Not enrolled'}
          status={mfaEnrolled ? 'on' : 'off'}
          to="/settings/mfa"
          actionLabel={mfaEnrolled ? 'Manage' : 'Set up'}
        />
      </Card>

      <Card title="Integrations" hint="Wire SFP up to the systems your data already lives in.">
        <Row
          label="Dropbox"
          value={
            dropboxConnected === null
              ? 'Checking…'
              : dropboxConnected
              ? 'Connected — SFP can browse and crawl your Dropbox tree.'
              : 'Not connected — connect to browse and crawl Dropbox files.'
          }
          status={dropboxConnected ? 'on' : 'off'}
          to="/integrations/dropbox"
          actionLabel={dropboxConnected ? 'Manage' : 'Connect'}
        />
        <Row
          label="Plaid"
          value="Coming soon — link bank/credit/loan accounts to auto-ingest statements and bills."
          status="off"
          to="/integrations/plaid"
          actionLabel="Set up"
        />
        <Row
          label="Gmail (bills inbox)"
          value="Forward bills to a dedicated inbox; Claude classifies them automatically."
          status="off"
          to="/integrations/gmail"
          actionLabel="Set up"
        />
        <Row
          label="Storage map"
          value="See exactly where every uploaded byte lives across Cloud Storage and Firestore."
          to="/integrations/storage"
          actionLabel="Open"
        />
        <Row
          label="File explorer"
          value="Browse the Cloud Storage tree directly. Admin can upload and delete."
          to="/files"
          actionLabel="Open"
        />
        {isAdmin && (
          <Row
            label="AI & models"
            value="Pick which LLM classifies documents (Grok or Claude), set models, routing, and API keys."
            to="/settings/ai"
            actionLabel="Configure"
          />
        )}
      </Card>

      {isAdmin && (
        <Card title="People" hint="Set up shulkinfamilypartners.com accounts and the family directory.">
          <Row
            label="Invite a new user"
            value="Create a new SFP account with @shulkinfamilypartners.com email, role, and granular permissions. Sends a reset link."
            to="/admin/invite"
            actionLabel="Invite"
          />
          <Row
            label="All users"
            value="Manage role + per-user permissions for everyone with access."
            to="/admin/users"
            actionLabel="Open"
          />
          <Row
            label="Family members"
            value="People directory — passports, licenses, medical, financial, estate, addresses."
            to="/admin/family"
            actionLabel="Open"
          />
        </Card>
      )}

      <Card title="Brand and home page">
        <Row
          label="SFP logo"
          value="Upload a logo — it becomes the sidebar mark and the browser tab favicon."
          to="/"
          actionLabel="Edit on home base"
        />
        <Row
          label="Tile and group layout"
          value="Customize which apps, groups, and widgets appear on the home base."
          to="/"
          actionLabel="Edit on home base"
        />
        <Row
          label="Appearance (colors / palette)"
          value="Pick the brand orange, sidebar shade, and canvas color."
          to="/settings/appearance"
          actionLabel="Open"
        />
      </Card>

      {isAdmin && (
        <Card title="Security">
          <Row
            label="Security actions checklist"
            value="Track and complete the open security tasks across SFP."
            to="/security"
            actionLabel="Open"
          />
          <Row
            label="Audit log"
            value="Every doc view, upload, role change, and invite logged."
            to="/admin/audit"
            actionLabel="Open"
          />
        </Card>
      )}
    </div>
  );
}

function Card({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-stone-800">{title}</h2>
        {hint && <div className="text-[11px] text-stone-500">{hint}</div>}
      </div>
      <div className="mt-3 divide-y divide-stone-100">{children}</div>
    </section>
  );
}

function Row({
  label,
  value,
  status,
  to,
  actionLabel,
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  status?: 'on' | 'off';
  to?: string;
  actionLabel: string;
  onClick?: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-stone-800">{label}</div>
          {status && (
            <span
              className={
                status === 'on'
                  ? 'rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-emerald-700'
                  : 'rounded bg-stone-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-stone-600'
              }
            >
              {status === 'on' ? 'On' : 'Off'}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-stone-500">{value}</div>
      </div>
      {to ? (
        <Link
          to={to}
          className="rounded border border-stone-300 px-3 py-1 text-xs font-medium text-stone-700 hover:bg-stone-50"
        >
          {actionLabel} →
        </Link>
      ) : (
        <button
          type="button"
          onClick={onClick}
          className="rounded border border-stone-300 px-3 py-1 text-xs font-medium text-stone-700 hover:bg-stone-50"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
